import {
  Checkbox,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HelpTooltip,
  Input,
  NetworkSelect,
} from "@gearbox-protocol/deploy-tools-ui";
import type { FC } from "react";
import type { UseFormReturn } from "react-hook-form";
import { LIVE_NETWORKS } from "../../constants";
import AnvilFlags from "./AnvilFlags";
import ForkAlias from "./ForkAlias";
import ForkBackend from "./ForkBackend";
import type { ForkSchema } from "./schema";
import ForkScripts from "./scripts";

const CHECKBOXES: Array<{
  name: "debug" | "disableRPCProtection" | "disableSync";
  label: string;
}> = [
  { name: "debug", label: "Debug" },
  { name: "disableRPCProtection", label: "Disable RPC protection" },
  { name: "disableSync", label: "Disable sync" },
];

interface ForkConstructorProps {
  form: UseFormReturn<ForkSchema>;
  hideAlias?: boolean;
}

export const ForkConstructor: FC<ForkConstructorProps> = props => {
  const { form, hideAlias } = props;
  return (
    <>
      <FormField
        control={form.control}
        name="network"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Network</FormLabel>
            <FormControl>
              <NetworkSelect {...field} includeNetworks={LIVE_NETWORKS} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Input placeholder="fork description (optional)" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="blockNumber"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Block number
              <HelpTooltip>
                <p className="whitespace-pre-line">
                  {`Block offset expression to determine starting block of a fork
Expression has following rules:
<empty string> = latest block
12244555 = absolute block number
-7200 = latest block -7200 blocks
-1h30m = latest block -1 hour 30 minutes
@ = previous Monday 12:00 UTC
@2h = previous Monday 12:00 UTC + 2 hours
@-7200 = previous Monday 12:00 UTC -7200 blocks`}
                </p>
              </HelpTooltip>
            </FormLabel>
            <FormControl>
              <Input
                placeholder="block number expression (optional)"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="timeout"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Timeout</FormLabel>
            <FormControl>
              <Input placeholder="fork timeout" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <ForkScripts control={form.control} />

      <ForkBackend control={form.control} />

      <div className="flex flex-col gap-y-2 px-4 py-2 border rounded-md">
        <FormField
          control={form.control}
          name="anvilFlags"
          render={({ field }) => <AnvilFlags field={field} />}
        />

        <div className="flex flex-row items-center gap-x-6">
          {CHECKBOXES.map(({ name, label }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-x-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>{label}</FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>

      {!hideAlias && <ForkAlias form={form} />}
    </>
  );
};
